/**
 * The emailed link lands here with `?token=…`. One form, two fields, and the shared password
 * rule checked before the request so the hint and the refusal are the same words the
 * change-password form uses. A spent or expired token is a 400 from the server, shown with
 * the way out (ask for a new link) rather than as a dead end.
 */

import * as React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { resetPasswordSchema } from '@ias/shared';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AuthError, AuthScreen, authInputClassName } from '@/components/layout/auth-screen';
import { ApiRequestError, post } from '@/lib/api/client';
import { useI18n } from '@/lib/i18n';

export function ResetPasswordPage() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [password, setPassword] = React.useState('');
  const [confirm, setConfirm] = React.useState('');
  const [done, setDone] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [linkRejected, setLinkRejected] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setLinkRejected(false);

    if (password !== confirm) {
      setError(t('auth.passwordsDoNotMatch'));
      return;
    }

    const parsed = resetPasswordSchema.safeParse({ token, password });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? t('error.generic'));
      return;
    }

    setSubmitting(true);
    try {
      await post('/auth/reset-password', parsed.data);
      setDone(true);
    } catch (caught) {
      if (caught instanceof ApiRequestError) {
        setError(caught.message);
        setLinkRejected(caught.code === 'invalid_reset_token');
      } else {
        setError(t('error.generic'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const backToSignIn = (
    <Button asChild variant="outline" className="w-full border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white">
      <Link to="/">{t('auth.backToSignIn')}</Link>
    </Button>
  );

  const requestNewLink = (
    <Link to="/forgot-password" className="text-[13px] text-white/70 underline-offset-4 hover:text-white hover:underline">
      {t('auth.requestNewLink')}
    </Link>
  );

  if (!token) {
    return (
      <AuthScreen>
        <div className="space-y-4">
          <p className="text-sm leading-relaxed text-white/85">{t('auth.resetLinkMissing')}</p>
          <p className="text-center">{requestNewLink}</p>
          {backToSignIn}
        </div>
      </AuthScreen>
    );
  }

  if (done) {
    return (
      <AuthScreen>
        <div className="space-y-4">
          <p className="text-sm leading-relaxed text-white/85">{t('auth.resetDone')}</p>
          {backToSignIn}
        </div>
      </AuthScreen>
    );
  }

  return (
    <AuthScreen>
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="space-y-1">
          <h1 className="font-display text-lg text-white">{t('auth.resetPasswordTitle')}</h1>
          <p className="text-[13px] leading-relaxed text-white/65">{t('auth.resetPasswordHint')}</p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="password" className="text-white/80">{t('auth.newPassword')}</Label>
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            required
            autoFocus
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            aria-invalid={Boolean(error)}
            className={authInputClassName}
          />
          <p className="text-xs text-white/50">{t('auth.changePasswordHint')}</p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="confirm" className="text-white/80">{t('auth.confirmPassword')}</Label>
          <Input
            id="confirm"
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
            aria-invalid={Boolean(error)}
            className={authInputClassName}
          />
        </div>

        {error && <AuthError>{error}</AuthError>}
        {linkRejected && <p className="text-center">{requestNewLink}</p>}

        <Button type="submit" className="bg-brand-gradient w-full border-0 shadow-lg shadow-[color:var(--brand-blue)]/25 hover:opacity-95" loading={submitting}>
          {t('auth.changePassword')}
        </Button>
      </form>
    </AuthScreen>
  );
}
